import { type ReactNode } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Bookmark, Globe, Mail, Phone, X } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import type { EventServiceListItem } from '@/types'

type Props = {
  services: EventServiceListItem[]
}

export default function DashboardSavedServicesIndex({ services }: Props) {
  function unsave(slug: string) {
    router.delete(`/services/${slug}/save`, { preserveScroll: true })
  }

  return (
    <>
      <Head title="Saved Services" />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Saved Services</h1>
        <p className="mt-1 text-base text-muted-foreground">Event services you've bookmarked for reference.</p>

        {services.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map(service => {
              const location = [service.city, service.state].filter(Boolean).join(', ')
              return (
                <div
                  key={service.id}
                  className="group relative rounded-2xl bg-card ring-1 ring-black/5 shadow-sm"
                >
                  <Link href={`/services/${service.slug}`} className="block">
                    <div className="relative h-36 overflow-hidden rounded-t-2xl bg-muted/30">
                      {service.cover_image_url ? (
                        <img
                          src={service.cover_image_url}
                          alt={service.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          {service.logo_url ? (
                            <img src={service.logo_url} alt={service.name} className="max-h-16 max-w-28 object-contain" />
                          ) : (
                            <Bookmark className="size-8 text-primary/25" />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-semibold tracking-tight">{service.name}</h3>
                      <p className="text-base text-muted-foreground">{service.service_type}</p>
                      {location && <p className="mt-1 text-sm text-muted-foreground">{location}</p>}

                      <div className="mt-3 flex flex-wrap gap-2">
                        {service.contact_email && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
                            <Mail className="size-3" /> Email
                          </span>
                        )}
                        {service.phone && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
                            <Phone className="size-3" /> Call
                          </span>
                        )}
                        {service.website && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
                            <Globe className="size-3" /> Website
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-7 w-7 bg-white/80 hover:bg-white shadow-sm"
                    onClick={() => unsave(service.slug)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="mt-12 text-center">
            <Bookmark className="mx-auto size-10 text-primary/25" />
            <p className="mt-3 text-muted-foreground">You haven't saved any services yet.</p>
            <Link href="/services">
              <Button variant="outline" className="mt-4">Browse Services</Button>
            </Link>
          </div>
        )}
      </div>
    </>
  )
}

DashboardSavedServicesIndex.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
